import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  TextField,
  Button,
  Grid,
  Box,
  DialogActions,
  Divider,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Autocomplete,
} from "@mui/material";
import { FormattedMessage } from "react-intl";
import { Resume } from "../../../../../types/resumes-managment/resume";
import { useUsers } from "../../../../../hooks/query-hooks/useUsers";
import { useResumesForAutocomplete } from "../../../../../hooks/query-hooks/useResumes";
import { useDebounce } from "../../../../../hooks";

export type JobSeekerFiltersType = {
  full_name: string;
  full_name_arabic: string;
  user_name: string;
  resume_id: string;
};

export type FiltersModalProps = {
  open: boolean;
  handleClose: () => void;
  onApplyFilters: (filters: JobSeekerFiltersType) => void;
  onClearFilters: () => void;
};

const FiltersModal = ({
  open,
  handleClose,
  onApplyFilters,
}: FiltersModalProps) => {
  const [fullName, setFullName] = useState("");
  const [fullNameArabic, setFullNameArabic] = useState("");
  const [resumeSearchTerm, setResumeSearchTerm] = useState("");

  const [selectedUser, setSelectedUser] = useState("");
  const [selectedResume, setSelectedResume] = useState<Resume | null>(null);

  const debouncedResumeSearchTerm = useDebounce(resumeSearchTerm, 500);

  const { data: users = [] } = useUsers("JOBSEEKER");
  const {
    items: resumes,
    loading: resumesLoading,
    loadMore: loadMoreResumes,
    hasMore: hasMoreResumes,
  } = useResumesForAutocomplete(20, debouncedResumeSearchTerm);

  const handleApply = () => {
    onApplyFilters({
      full_name: fullName,
      full_name_arabic: fullNameArabic,
      user_name: selectedUser,
      resume_id: selectedResume?.id?.toString() || "",
    });
    handleClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontSize: "1.3rem", fontWeight: 500 }}>
        <FormattedMessage id="Filters" />
      </DialogTitle>
      <Divider sx={{ marginInline: "1rem" }} />
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                label={<FormattedMessage id="Full Name English" />}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                value={fullNameArabic}
                onChange={(e) => setFullNameArabic(e.target.value)}
                label={<FormattedMessage id="Full Name Arabic" />}
                fullWidth
              />
            </Grid>
          </Grid>

          <Grid container spacing={2} marginTop=".5rem">
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel id="user-select-label">
                  <FormattedMessage id="Select user" />
                </InputLabel>
                <Select
                  labelId="user-select-label"
                  id="user-select"
                  value={selectedUser || ""}
                  label="Select user"
                  onChange={(e) => {
                    setSelectedUser(e.target.value);
                  }}
                  renderValue={(selected) => {
                    const user = users.find((u) => u.user_name === selected);
                    return user ? user.user_name : "";
                  }}
                >
                  {users.map((option) => (
                    <MenuItem key={option.id} value={option.user_name}>
                      {option.user_name || option.email || String(option.id)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                options={resumes}
                getOptionLabel={(option) =>
                  option.objectives || option.id?.toString() || ""
                }
                value={selectedResume}
                onChange={(_, newValue) => setSelectedResume(newValue)}
                onInputChange={(event, newInputValue, reason) => {
                  if (reason === "input") {
                    setResumeSearchTerm(newInputValue);
                  }
                }}
                loading={resumesLoading}
                filterOptions={(x) => x}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={<FormattedMessage id="Select resume" />}
                    InputProps={{
                      ...params.InputProps,
                    }}
                  />
                )}
                ListboxProps={{
                  onScroll: (event) => {
                    const listboxNode = event.currentTarget;
                    if (
                      listboxNode.scrollTop + listboxNode.clientHeight >=
                      listboxNode.scrollHeight - 1
                    ) {
                      if (hasMoreResumes) {
                        loadMoreResumes();
                      }
                    }
                  },
                }}
              />
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions sx={{ padding: "1rem" }}>
        <Button
          variant="outlined"
          color="primary"
          onClick={handleClose}
          sx={{ padding: ".3rem 3rem " }}
        >
          <FormattedMessage id="Cancel" />
        </Button>
        <Button
          onClick={handleApply}
          variant="contained"
          color="primary"
          sx={{ padding: ".3rem 3rem " }}
        >
          <FormattedMessage id="Apply" />
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FiltersModal;
